import { useState, useEffect } from 'react'

export type ViewportTier = 'small' | 'mobile' | 'desktop' | 'wide'

// `mobile` starts at 370 (not 380) so the iPhone SE / X-class widths (375px) render the roomier
// `mobile` tier — bigger logo (38 vs 28), amount font (28 vs 24), token selectors and CTA — matching
// the design we want on those phones. This only fits now that the header's currency control is
// truncated to a symbol (freeing ~28px); before that, 375px on `mobile` clipped the rightmost icon.
// Ultra-narrow phones (≤369px: 320 SE-1, 360 Android/12-mini) stay on the compact `small` tier so
// they don't overflow. Revisit downward if 370–379 ever feels cramped.
const BREAKPOINTS = { small: 0, mobile: 370, desktop: 480, wide: 1280 } as const

function getTier(w: number): ViewportTier {
  if (w >= BREAKPOINTS.wide) return 'wide'
  if (w >= BREAKPOINTS.desktop) return 'desktop'
  if (w >= BREAKPOINTS.mobile) return 'mobile'
  return 'small'
}

export function useViewportSize() {
  const [tier, setTier] = useState<ViewportTier>(() =>
    typeof window !== 'undefined' ? getTier(window.innerWidth) : 'desktop'
  )

  useEffect(() => {
    const onResize = () => setTier(getTier(window.innerWidth))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return tier
}
